import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'react-hot-toast'
import { Globe, Network, FileText, Upload, Database, Cloud, Plug, Play, Loader2, Link2, CheckCircle2, XCircle, Wifi } from 'lucide-react'
import { pollJob } from '../lib/jobs'
import { getSession } from '../lib/session'

// A reusable "where's your content?" picker. A website URL is just one tab.
// Content sources (url / sitemap / paste / file) and connectors (database /
// cloud storage / Confluence·Jira) all write to the SAME tenant knowledge
// namespace — so this one component feeds a chatbot, Sync search, Intake, and
// the agent alike. RAG uses per-collection namespaces, so it keeps its own UI.
//
// Props:
//   instanceId  — optional; attaches content to a specific chatbot (Answers)
//   onDone      — called after a source is successfully queued
//   className   — wrapper classes

const CONTENT_TABS = [
  ['url', 'Website URL', Globe],
  ['sitemap', 'Sitemap', Network],
  ['text', 'Paste text', FileText],
  ['file', 'Upload file', Upload],
]
const CONNECTORS = [
  ['sql', 'SQL database', Database],
  ['s3', 'AWS S3', Cloud],
  ['gcs', 'Google Cloud', Cloud],
  ['azure', 'Azure Blob', Cloud],
  ['confluence', 'Confluence', Plug],
  ['jira', 'Jira', Plug],
  ['monday', 'monday.com', Plug],
  ['asana', 'Asana', Plug],
  ['salesforce', 'Salesforce', Plug],
  ['sap', 'SAP S/4HANA', Plug],
  ['slack', 'Slack', Plug],
]
const COMING_SOON = [] // all live now
const safeJson = (s) => { try { return JSON.parse(s || '{}') } catch { return {} } }
const SAAS_APPS = ['confluence', 'jira', 'monday', 'asana', 'salesforce', 'sap', 'slack']

export default function ContentSource({ instanceId, dataInstanceId, onDone, className = '', initialTab, soloProvider }) {
  // soloProvider → show ONLY that one connector's form (click-a-logo flow).
  const [tab, setTab] = useState(soloProvider ? 'connect' : (initialTab || 'url'))
  const [provider, setProvider] = useState(soloProvider || 'sql') // active connector when tab === 'connect'
  const [form, setForm] = useState({})            // url/sitemap/text fields
  const [conn, setConn] = useState({})            // connector fields
  const [file, setFile] = useState(null)
  const [busy, setBusy] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [catalog, setCatalog] = useState([]) // config-driven connectors (no bespoke code)
  useEffect(() => { axios.get('/api/v1/sync/connectors/catalog').then(r => setCatalog(r.data.connectors || [])).catch(() => {}) }, [])
  const catEntry = catalog.find(c => c.id === provider)
  const setF = (k, v) => setForm(s => ({ ...s, [k]: v }))
  const setC = (k, v) => { setTestResult(null); setConn(c => ({ ...c, [k]: v })) }
  // Collect a catalog connector's creds/config from `conn` by its field spec.
  const catPayload = (e) => {
    const credentials = {}, config = {}
    ;(e.creds || []).forEach(f => { if (conn[f.key]) credentials[f.key] = conn[f.key] })
    ;(e.config || []).forEach(f => { if (conn[f.key]) config[f.key] = conn[f.key] })
    return { credentials, config }
  }

  // Build a uniform { type, credentials, config } for the symmetrical test endpoint.
  const testPayload = () => {
    if (provider === 'sql') return { type: 'sql', credentials: { engine: conn.engine, connectionString: conn.connectionString, host: conn.host, port: conn.port ? Number(conn.port) : undefined, user: conn.user, password: conn.password, database: conn.database, ssl: conn.ssl }, config: {} }
    if (['s3', 'gcs', 'azure'].includes(provider)) {
      const credentials = provider === 's3' ? { region: conn.region, accessKeyId: conn.accessKeyId, secretAccessKey: conn.secretAccessKey }
        : provider === 'gcs' ? { projectId: conn.projectId, serviceAccount: safeJson(conn.serviceAccount) }
        : conn.connectionString ? { connectionString: conn.connectionString } : { account: conn.account, accountKey: conn.accountKey }
      return { type: provider, credentials, config: { bucket: conn.bucket, prefix: conn.prefix } }
    }
    if (provider === 'confluence' || provider === 'jira') return { type: provider, credentials: { baseUrl: conn.baseUrl, email: conn.email, apiToken: conn.apiToken }, config: provider === 'confluence' ? { spaceKey: conn.spaceKey } : { jql: conn.jql } }
    if (provider === 'monday') return { type: 'monday', credentials: { apiToken: conn.apiToken }, config: { boardIds: conn.boardIds } }
    if (provider === 'asana') return { type: 'asana', credentials: { accessToken: conn.accessToken }, config: { projectId: conn.projectId, workspaceId: conn.workspaceId } }
    if (provider === 'salesforce') return { type: 'salesforce', credentials: { clientId: conn.clientId, clientSecret: conn.clientSecret, username: conn.username, password: conn.password, securityToken: conn.securityToken }, config: { loginUrl: conn.loginUrl, soql: conn.soql } }
    if (provider === 'sap') return { type: 'sap', credentials: { baseUrl: conn.baseUrl, user: conn.user, password: conn.password, token: conn.token }, config: { odataPath: conn.odataPath } }
    if (provider === 'slack') return { type: 'slack', credentials: { botToken: conn.botToken }, config: { channels: conn.channels } }
    if (catEntry) { const p = catPayload(catEntry); return { type: provider, credentials: p.credentials, config: p.config } }
    return { type: provider, credentials: {}, config: {} }
  }
  const testConn = async () => {
    setTesting(true); setTestResult(null)
    try {
      const ns = getSession()?.namespaceId
      const { data } = await axios.post(`/api/v1/tenants/${ns}/connectors/test`, testPayload())
      setTestResult(data)
    } catch (e) {
      setTestResult({ status: 'UNREACHABLE', detail: e.response?.data?.detail || e.message })
    } finally { setTesting(false) }
  }
  const finish = (label) => { toast.success(label); setForm({}); setConn({}); setFile(null); onDone?.() }

  // ---- content sources (url / sitemap / paste / file) -----------------------
  const submitContent = async () => {
    setBusy(true)
    try {
      if (tab === 'url' || tab === 'sitemap') {
        if (!form.sourceUrl?.trim()) throw new Error(tab === 'sitemap' ? 'Enter your sitemap URL' : 'Enter a website URL')
        const res = await axios.post('/api/v1/answers/ingest', { sourceType: tab, sourceUrl: form.sourceUrl.trim(), instanceId, dataInstanceId })
        const done = res.data.jobId ? await pollJob(res.data.jobId) : null
        finish(done ? `Ingested ${done.chunks ?? ''} chunk(s)` : 'Crawl queued — indexing in the background')
      } else if (tab === 'text') {
        if (!form.content?.trim()) throw new Error('Paste some text first')
        const res = await axios.post('/api/v1/answers/ingest', { sourceType: 'text', content: form.content, instanceId, dataInstanceId })
        const done = res.data.jobId ? await pollJob(res.data.jobId) : null
        finish(done ? `Ingested ${done.chunks ?? ''} chunk(s)` : 'Text ingested')
      } else if (tab === 'file') {
        if (!file) throw new Error('Choose a file to upload')
        const fd = new FormData()
        fd.append('file', file)
        if (instanceId) fd.append('instanceId', instanceId)
        if (dataInstanceId) fd.append('dataInstanceId', dataInstanceId)
        const res = await axios.post('/api/v1/answers/ingest/file', fd)
        const done = res.data.jobId ? await pollJob(res.data.jobId) : null
        finish(done ? `Ingested ${done.chunks ?? ''} chunk(s)` : `“${res.data.title || file.name}” queued`)
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Failed')
    } finally { setBusy(false) }
  }

  // ---- connectors (reuse the Sync connector endpoints) ----------------------
  const requestInterest = async () => {
    setBusy(true)
    try {
      const r = await axios.post('/api/v1/sync/connectors/interest', { app: provider, note: conn.note || '' })
      toast.success(r.data.message || "Thanks — we'll be in touch."); setConn({})
    } catch (err) { toast.error(err.response?.data?.error || 'Failed') } finally { setBusy(false) }
  }

  const submitConnector = async () => {
    setBusy(true)
    try {
      let res
      if (provider === 'sql') {
        if (!conn.query || (!conn.connectionString && !conn.host)) throw new Error('A query and either a connection string or host/user/password/database are required')
        res = await axios.post('/api/v1/sync/connectors/sql', {
          engine: conn.engine || undefined,
          connectionString: conn.connectionString || undefined,
          host: conn.host || undefined, port: conn.port ? Number(conn.port) : undefined,
          user: conn.user || undefined, password: conn.password || undefined, database: conn.database || undefined,
          ssl: conn.ssl || undefined,
          query: conn.query, titleColumn: conn.titleColumn || undefined,
          dataInstanceId, instanceId,
        })
      } else if (SAAS_APPS.includes(provider)) {
        let credentials, config
        if (provider === 'confluence' || provider === 'jira') {
          if (!conn.baseUrl || !conn.email || !conn.apiToken) throw new Error('Base URL, email and API token are required')
          credentials = { baseUrl: conn.baseUrl, email: conn.email, apiToken: conn.apiToken }
          config = provider === 'confluence' ? { spaceKey: conn.spaceKey || undefined } : { jql: conn.jql || undefined }
        } else if (provider === 'monday') {
          if (!conn.apiToken) throw new Error('A monday.com API token is required')
          credentials = { apiToken: conn.apiToken }
          config = { boardIds: conn.boardIds || undefined }
        } else if (provider === 'asana') {
          if (!conn.accessToken) throw new Error('An Asana personal access token is required')
          if (!conn.projectId && !conn.workspaceId) throw new Error('A project ID (or workspace ID) is required')
          credentials = { accessToken: conn.accessToken }
          config = { projectId: conn.projectId || undefined, workspaceId: conn.workspaceId || undefined }
        } else if (provider === 'salesforce') {
          if (!conn.clientId || !conn.clientSecret || !conn.username || !conn.password) throw new Error('Consumer key/secret, username and password are required')
          credentials = { clientId: conn.clientId, clientSecret: conn.clientSecret, username: conn.username, password: conn.password, securityToken: conn.securityToken || undefined }
          config = { loginUrl: conn.loginUrl || undefined, soql: conn.soql || undefined }
        } else if (provider === 'sap') {
          if (!conn.baseUrl || !conn.odataPath) throw new Error('Base URL and an OData entity path are required')
          if (!conn.token && !(conn.user && conn.password)) throw new Error('SAP needs a user + password, or a bearer token')
          credentials = { baseUrl: conn.baseUrl, user: conn.user || undefined, password: conn.password || undefined, token: conn.token || undefined }
          config = { odataPath: conn.odataPath }
        } else if (provider === 'slack') {
          if (!conn.botToken) throw new Error('A Slack bot token (xoxb-…) is required')
          credentials = { botToken: conn.botToken }
          config = { channels: conn.channels || undefined }
        } else if (catEntry) { // config-driven catalog connector
          const p = catPayload(catEntry); credentials = p.credentials; config = p.config
        }
        res = await axios.post('/api/v1/sync/connectors/saas', { app: provider, credentials, config, dataInstanceId, instanceId })
      } else {
        const credentials =
          provider === 's3' ? { region: conn.region, accessKeyId: conn.accessKeyId, secretAccessKey: conn.secretAccessKey }
          : provider === 'gcs' ? { projectId: conn.projectId, serviceAccount: JSON.parse(conn.serviceAccount || '{}') }
          : conn.connectionString ? { connectionString: conn.connectionString } : { account: conn.account, accountKey: conn.accountKey }
        res = await axios.post('/api/v1/sync/connectors/cloud', {
          provider, bucket: conn.bucket, prefix: conn.prefix || undefined, credentials,
          dataInstanceId, instanceId,
        })
      }
      toast.loading('Importing…')
      const done = res.data.jobId ? await pollJob(res.data.jobId) : null
      toast.dismiss()
      finish(done ? `Imported ${done.objects ?? done.rows ?? ''} item(s) / ${done.chunks ?? ''} chunk(s)` : 'Import queued')
    } catch (err) {
      toast.dismiss()
      toast.error(err.response?.data?.error || err.message || 'Connect failed')
    } finally { setBusy(false) }
  }

  const onConnect = COMING_SOON.includes(provider) ? requestInterest : submitConnector

  return (
    <div className={className}>
      {/* Primary source tabs — the easy ones first, then "connect a system" */}
      {!soloProvider && <div className="flex flex-wrap gap-2">
        {CONTENT_TABS.map(([k, label, Icon]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors ${
              tab === k ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}>
            <Icon size={14} /> {label}
          </button>
        ))}
        <button type="button" onClick={() => setTab('connect')}
          className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors ${
            tab === 'connect' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}>
          <Link2 size={14} /> Connect a system
        </button>
      </div>}

      {/* ---- content-source fields ---- */}
      {tab === 'url' && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Website URL</label>
          <input type="url" className="input" placeholder="https://yourcompany.com"
            value={form.sourceUrl || ''} onChange={e => setF('sourceUrl', e.target.value)} />
          <p className="text-xs text-gray-400 mt-1">We crawl the site’s pages and index the text. Public pages only.</p>
        </div>
      )}
      {tab === 'sitemap' && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Sitemap URL</label>
          <input type="url" className="input" placeholder="https://yourcompany.com/sitemap.xml"
            value={form.sourceUrl || ''} onChange={e => setF('sourceUrl', e.target.value)} />
          <p className="text-xs text-gray-400 mt-1">Crawls every page listed in your sitemap — the fastest way to index a whole site.</p>
        </div>
      )}
      {tab === 'text' && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Paste text</label>
          <textarea className="input h-36" placeholder="Paste FAQs, policies, product details — anything you want it to know…"
            value={form.content || ''} onChange={e => setF('content', e.target.value)} />
        </div>
      )}
      {tab === 'file' && (
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Upload a document</label>
          <input type="file" accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.md"
            onChange={e => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-primary-500/10 file:text-primary-600 hover:file:bg-primary-500/20" />
          <p className="text-xs text-gray-400 mt-1">PDF, Word, Excel, CSV, TXT or Markdown. {file ? `Selected: ${file.name}` : 'One file at a time.'}</p>
        </div>
      )}

      {/* ---- connectors (reused from Sync) ---- */}
      {tab === 'connect' && (
        <div className="mt-4 rounded-lg border border-gray-200 p-4 bg-gray-50/60 space-y-4">
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Pull content from a system you already use</p>
            <p className="text-xs text-gray-500 mb-2">Credentials are encrypted in your vault. We only read text rows/files and index them.</p>
            {!soloProvider && <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-lg">
              {[...CONNECTORS.map(([k, label]) => [k, label]), ...catalog.map(c => [c.id, c.name])].map(([k, label]) => (
                <button key={k} type="button" onClick={() => { setProvider(k); setConn({}) }}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1 ${provider === k ? 'bg-white shadow-sm text-gray-900' : 'text-gray-600'}`}>
                  {label}
                  {COMING_SOON.includes(k) && <span className="text-[9px] uppercase tracking-wide bg-amber-500/15 text-amber-600 px-1 py-0.5 rounded">soon</span>}
                </button>
              ))}
            </div>}
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            {provider === 'sql' && <>
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-gray-600 mb-1">Engine</label>
                <select className="input" value={conn.engine || 'postgres'} onChange={e => setC('engine', e.target.value)}>
                  <option value="postgres">PostgreSQL (incl. RDS/Cloud SQL Postgres)</option>
                  <option value="mysql">MySQL (AWS RDS / GCP Cloud SQL)</option>
                  <option value="sqlserver">SQL Server (Azure SQL)</option>
                </select>
              </div>
              <input className="input" placeholder="Host — e.g. mydb.abc.us-east-1.rds.amazonaws.com" value={conn.host || ''} onChange={e => setC('host', e.target.value)} />
              <input className="input" placeholder="Port (3306 MySQL · 1433 SQL Server · 5432 PG)" value={conn.port || ''} onChange={e => setC('port', e.target.value)} />
              <input className="input" placeholder="User" value={conn.user || ''} onChange={e => setC('user', e.target.value)} />
              <input className="input" type="password" placeholder="Password" value={conn.password || ''} onChange={e => setC('password', e.target.value)} />
              <input className="input" placeholder="Database name" value={conn.database || ''} onChange={e => setC('database', e.target.value)} />
              <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={conn.ssl || false} onChange={e => setC('ssl', e.target.checked)} /> Require SSL/TLS</label>
              <p className="text-[11px] text-gray-400 sm:col-span-2">…or paste a full connection string instead of the fields above:</p>
              <input className="input sm:col-span-2" placeholder="postgres://user:pass@host:5432/db  (optional)" value={conn.connectionString || ''} onChange={e => setC('connectionString', e.target.value)} />
              <textarea className="input sm:col-span-2 h-20" placeholder="Read-only query — SELECT id, title, body FROM articles" value={conn.query || ''} onChange={e => setC('query', e.target.value)} />
              <input className="input sm:col-span-2" placeholder="Title column (optional)" value={conn.titleColumn || ''} onChange={e => setC('titleColumn', e.target.value)} />
            </>}
            {provider === 's3' && <>
              <input className="input" placeholder="Bucket name" value={conn.bucket || ''} onChange={e => setC('bucket', e.target.value)} />
              <input className="input" placeholder="Region (e.g. us-east-1)" value={conn.region || ''} onChange={e => setC('region', e.target.value)} />
              <input className="input" placeholder="Access key ID" value={conn.accessKeyId || ''} onChange={e => setC('accessKeyId', e.target.value)} />
              <input className="input" type="password" placeholder="Secret access key" value={conn.secretAccessKey || ''} onChange={e => setC('secretAccessKey', e.target.value)} />
              <input className="input sm:col-span-2" placeholder="Prefix / folder (optional)" value={conn.prefix || ''} onChange={e => setC('prefix', e.target.value)} />
            </>}
            {provider === 'gcs' && <>
              <input className="input" placeholder="Bucket name" value={conn.bucket || ''} onChange={e => setC('bucket', e.target.value)} />
              <input className="input" placeholder="Project ID (optional)" value={conn.projectId || ''} onChange={e => setC('projectId', e.target.value)} />
              <textarea className="input sm:col-span-2 h-24 font-mono text-xs" placeholder="Service-account JSON key (paste the whole file)" value={conn.serviceAccount || ''} onChange={e => setC('serviceAccount', e.target.value)} />
              <input className="input sm:col-span-2" placeholder="Prefix / folder (optional)" value={conn.prefix || ''} onChange={e => setC('prefix', e.target.value)} />
            </>}
            {provider === 'azure' && <>
              <input className="input" placeholder="Container name" value={conn.bucket || ''} onChange={e => setC('bucket', e.target.value)} />
              <input className="input" placeholder="Prefix / folder (optional)" value={conn.prefix || ''} onChange={e => setC('prefix', e.target.value)} />
              <input className="input sm:col-span-2" type="password" placeholder="Connection string (or use account + key below)" value={conn.connectionString || ''} onChange={e => setC('connectionString', e.target.value)} />
              <input className="input" placeholder="Account name" value={conn.account || ''} onChange={e => setC('account', e.target.value)} />
              <input className="input" type="password" placeholder="Account key" value={conn.accountKey || ''} onChange={e => setC('accountKey', e.target.value)} />
            </>}
            {(provider === 'confluence' || provider === 'jira') && <>
              <input className="input sm:col-span-2" placeholder="Base URL — https://your-org.atlassian.net" value={conn.baseUrl || ''} onChange={e => setC('baseUrl', e.target.value)} />
              <input className="input" placeholder="Atlassian account email" value={conn.email || ''} onChange={e => setC('email', e.target.value)} />
              <input className="input" type="password" placeholder="API token (id.atlassian.com → API tokens)" value={conn.apiToken || ''} onChange={e => setC('apiToken', e.target.value)} />
              {provider === 'confluence'
                ? <input className="input sm:col-span-2" placeholder="Space key (optional — blank = all spaces)" value={conn.spaceKey || ''} onChange={e => setC('spaceKey', e.target.value)} />
                : <input className="input sm:col-span-2" placeholder='JQL (optional — e.g. project = ABC order by created DESC)' value={conn.jql || ''} onChange={e => setC('jql', e.target.value)} />}
            </>}
            {provider === 'monday' && <>
              <input className="input sm:col-span-2" type="password" placeholder="API token (monday.com → Admin → API)" value={conn.apiToken || ''} onChange={e => setC('apiToken', e.target.value)} />
              <input className="input sm:col-span-2" placeholder="Board IDs (optional, comma-separated — blank = first 50 boards)" value={conn.boardIds || ''} onChange={e => setC('boardIds', e.target.value)} />
            </>}
            {provider === 'asana' && <>
              <input className="input sm:col-span-2" type="password" placeholder="Personal access token (Asana → Settings → Apps → Developer)" value={conn.accessToken || ''} onChange={e => setC('accessToken', e.target.value)} />
              <input className="input" placeholder="Project ID (recommended)" value={conn.projectId || ''} onChange={e => setC('projectId', e.target.value)} />
              <input className="input" placeholder="…or Workspace ID" value={conn.workspaceId || ''} onChange={e => setC('workspaceId', e.target.value)} />
            </>}
            {provider === 'salesforce' && <>
              <input className="input" placeholder="Consumer Key (Connected App client_id)" value={conn.clientId || ''} onChange={e => setC('clientId', e.target.value)} />
              <input className="input" type="password" placeholder="Consumer Secret" value={conn.clientSecret || ''} onChange={e => setC('clientSecret', e.target.value)} />
              <input className="input" placeholder="Username (user@domain.com)" value={conn.username || ''} onChange={e => setC('username', e.target.value)} />
              <input className="input" type="password" placeholder="Password" value={conn.password || ''} onChange={e => setC('password', e.target.value)} />
              <input className="input" type="password" placeholder="Security token (appended to password)" value={conn.securityToken || ''} onChange={e => setC('securityToken', e.target.value)} />
              <input className="input" placeholder="Login URL (blank = login.salesforce.com; sandbox = test.salesforce.com)" value={conn.loginUrl || ''} onChange={e => setC('loginUrl', e.target.value)} />
              <textarea className="input sm:col-span-2 h-16" placeholder="SOQL (optional — e.g. SELECT Id, Title, Summary FROM Knowledge__kav WHERE PublishStatus='Online')" value={conn.soql || ''} onChange={e => setC('soql', e.target.value)} />
            </>}
            {provider === 'sap' && <>
              <input className="input sm:col-span-2" placeholder="Base URL — https://your-s4hana-host.com" value={conn.baseUrl || ''} onChange={e => setC('baseUrl', e.target.value)} />
              <input className="input sm:col-span-2" placeholder="OData entity path — /sap/opu/odata/sap/API_.../A_Entity" value={conn.odataPath || ''} onChange={e => setC('odataPath', e.target.value)} />
              <input className="input" placeholder="Comm. user (basic auth)" value={conn.user || ''} onChange={e => setC('user', e.target.value)} />
              <input className="input" type="password" placeholder="Password" value={conn.password || ''} onChange={e => setC('password', e.target.value)} />
              <input className="input sm:col-span-2" type="password" placeholder="…or OAuth2 bearer token (instead of user/password)" value={conn.token || ''} onChange={e => setC('token', e.target.value)} />
            </>}
            {provider === 'slack' && <>
              <input className="input sm:col-span-2" type="password" placeholder="Bot token (xoxb-… — needs channels:history, channels:read)" value={conn.botToken || ''} onChange={e => setC('botToken', e.target.value)} />
              <input className="input sm:col-span-2" placeholder="Channel IDs (optional, comma-separated — blank = first 20 public channels)" value={conn.channels || ''} onChange={e => setC('channels', e.target.value)} />
            </>}
            {catEntry && <>
              {(catEntry.creds || []).map(f => (
                <input key={f.key} className="input sm:col-span-2" type={f.secret ? 'password' : 'text'} placeholder={f.label}
                  value={conn[f.key] || ''} onChange={e => setC(f.key, e.target.value)} />
              ))}
              {(catEntry.config || []).map(f => (
                <input key={f.key} className="input sm:col-span-2" placeholder={f.placeholder ? `${f.label} — ${f.placeholder}` : f.label}
                  value={conn[f.key] || ''} onChange={e => setC(f.key, e.target.value)} />
              ))}
            </>}
            {COMING_SOON.includes(provider) && <div className="sm:col-span-2 space-y-2">
              <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 px-3 py-2 text-sm text-amber-700">
                The <b className="capitalize">{provider}</b> connector isn't live yet. Tell us your use case and we'll prioritize it — and reach out when it's ready.
              </div>
              <textarea className="input h-20" placeholder={`How would you use the ${provider} connector? (optional)`} value={conn.note || ''} onChange={e => setC('note', e.target.value)} />
            </div>}
          </div>
        </div>
      )}

      {/* Connection-test result (symmetrical test — validate before importing) */}
      {tab === 'connect' && testResult && (
        <div className={`mt-3 flex items-start gap-2 text-sm rounded-lg px-3 py-2 ${testResult.status === 'CONNECTED' ? 'bg-green-500/10 text-green-700' : 'bg-red-500/10 text-red-700'}`}>
          {testResult.status === 'CONNECTED' ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : <XCircle size={16} className="mt-0.5 shrink-0" />}
          <span><b>{testResult.status}</b>{testResult.code ? ` (HTTP ${testResult.code})` : ''}{testResult.detail ? ` — ${testResult.detail}` : ''}</span>
        </div>
      )}

      {/* Actions */}
      <div className="mt-4 flex items-center gap-2">
        {tab === 'connect' && !COMING_SOON.includes(provider) && (
          <button type="button" disabled={testing || busy} onClick={testConn}
            className="btn-secondary flex items-center gap-2 disabled:opacity-60">
            {testing ? <Loader2 className="animate-spin" size={16} /> : <Wifi size={16} />} Test connection
          </button>
        )}
        <button type="button" disabled={busy} onClick={tab === 'connect' ? onConnect : submitContent}
          className="btn-primary flex items-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="animate-spin" size={16} /> : <Play size={16} />}
          {tab === 'connect'
            ? (COMING_SOON.includes(provider) ? 'Request access' : 'Connect & import')
            : 'Add this content'}
        </button>
      </div>
    </div>
  )
}
